import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { List, ListItemButton, ListItemText, Popover } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { FolderNavState } from 'features/documentList/container/DocumentListPage/types';
import { BreadcrumbW } from 'features/documentList/container/styled';
import { Links } from 'settings';

import { BreadcrumbInner, CrumbLink, CrumbSeparator, EllipsisBtn } from './styled';
import { getCollapsedCrumbs } from './utils';

export { getCollapsedCrumbs } from './utils';

export interface FolderBreadcrumbProps {
  tag: string;
  categoryLabel: string;
  /** Ancestors before the current folder, oldest first. Pass [] at category root. */
  trail: Array<{ id: string; name: string }>;
  /** Undefined when viewing a category root (no folder selected). */
  currentFolderName?: string;
  isPinnedSection?: boolean;
}

export const FolderBreadcrumb = ({
  tag,
  categoryLabel,
  trail,
  currentFolderName,
  isPinnedSection,
}: FolderBreadcrumbProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isPrivate = tag === 'private';
  const goToResources = () => navigate(Links.resources.index);
  const goToCategory = () => navigate(generatePath(Links.resources.documents.tag, { tag }));

  const goToTrailItem = (index: number) => {
    const crumb = trail[index];
    navigate(generatePath(Links.resources.documents.folderView, { tag, folderId: crumb.id }), {
      state: {
        trail: trail.slice(0, index),
        folderName: crumb.name,
      } as FolderNavState,
    });
  };

  const { showEllipsis, visibleItems, hiddenItems } = getCollapsedCrumbs(trail);

  return (
    <BreadcrumbW isPrivate={isPrivate || !!isPinnedSection}>
      <BreadcrumbInner>
        <span>
          {isPrivate
            ? t('breadcrumbs.profile')
            : isPinnedSection
            ? t('breadcrumbs.we')
            : t('breadcrumbs.resources')}{' '}
          /{' '}
        </span>
        {!isPrivate && !isPinnedSection && (
          <>
            <CrumbLink onClick={goToResources}>
              {t('resources.documentCategories.subtitle1')}
            </CrumbLink>
            <CrumbSeparator> / </CrumbSeparator>
          </>
        )}
        {currentFolderName ? (
          <CrumbLink onClick={goToCategory}>{categoryLabel}</CrumbLink>
        ) : (
          <span>{categoryLabel}</span>
        )}

        {currentFolderName && (
          <>
            {showEllipsis && (
              <>
                <CrumbSeparator> / </CrumbSeparator>
                <EllipsisBtn size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <MoreHorizIcon fontSize="small" />
                </EllipsisBtn>
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <List dense disablePadding>
                    {hiddenItems.map((crumb, i) => (
                      <ListItemButton
                        key={crumb.id}
                        onClick={() => {
                          setAnchorEl(null);
                          goToTrailItem(i);
                        }}
                      >
                        <ListItemText primary={crumb.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </Popover>
              </>
            )}

            {visibleItems.map((crumb, i) => {
              const trailIndex = hiddenItems.length + i;
              return (
                <span key={crumb.id}>
                  <CrumbSeparator> / </CrumbSeparator>
                  <CrumbLink onClick={() => goToTrailItem(trailIndex)}>{crumb.name}</CrumbLink>
                </span>
              );
            })}

            <CrumbSeparator> / </CrumbSeparator>
            <span>{currentFolderName}</span>
          </>
        )}
      </BreadcrumbInner>
    </BreadcrumbW>
  );
};
